import { config } from '@config'
import { TableViewColumnDefinition } from '@components/UI-Components/StandardTableView/models/TableViewColumnDefinition'
import { OrderType } from '@modules/common/models/enums/OrderManagement'
import { IStandardBuyingSession } from '@modules/common/models/interfaces/BuyingSession'
import { CentricEnumValue } from '@modules/common/models/interfaces/CentricEnumValue'
import { CentricNode } from '@modules/common/models/interfaces/CentricNode'
import formatCurrencyValueByLocale from '@utils/format-currency-value-by-locale'
import formatNumberByLocale from '@utils/format-number-by-locale'
import {
  getEnumDescriptionFromValue,
  getEnumOptionMap,
  isWholesaleCMOrAdmin,
  isRetailCMOrAdmin,
  isZAM,
  searchData,
  isAdmin,
  getUTCFromTimeStamp
} from '@services/commonServices'
import { strings } from '@stores'
import memoize from 'memoize-one'
import formatDateByLocale from '@utils/format-date-by-locale'
import { IDoor } from './doorManagementService'
import compact from 'lodash/compact'
import filter from 'lodash/filter'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import forEach from 'lodash/forEach'
import groupBy from 'lodash/groupBy'
import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import keys from 'lodash/keys'
import map from 'lodash/map'
import { transformConfig } from '@services/userRoleService'
import { DefaultConfig } from '@constants'
import { IBuyingGroupDeliveryWindow } from '@modules/showroom/basketAndOrders-new/components/CellTemplates/DeliveryDate/DeliveryDatePicker/DeliveryDatePicker'

type OrderDataWithStatusEnums = {
  clients: Array<IClient>
  customerGroupEnumValues: Array<CentricEnumValue>
  buyingSessionGroup: IBuyingSessionGroup
  exchangeRatesForGlobalCurrency: any
}

export enum SAPStatusDescription {
  NOT_RECEIVED = 'NOT RECEIVED',
  RECEIVED = 'RECEIVED',
  FAILED = 'FAILED',
  SEND = 'SEND',
  SENT = 'SENT'
}

enum SAPStatusDescriptionColorMapping {
  'NOT RECEIVED' = '#EF8A93',
  FAILED = '#A70000',
  RECEIVED = '#A5E170',
  SEND = '#4ba4f9',
  SENT = '#FBC569'
}

export interface IOption {
  label: string
  value: string
}

export interface IOrderManagementTableData {
  id: string
  name: string
  clientId: string
  code: string
  buyingSessionId?: string
  clientOrderId?: string
  date: string
  orderNumber: string
  salesAssistant: string
  doors: IOption[]
  value: string
  globalValue: string
  deliveryDate: string
  qty: string
  country: string
  zam: string
  activityGender: IOption[]
  orderType: OrderType
  shipTo: string
  deliveryWindow?: IBuyingGroupDeliveryWindow
  isDeliveryDateDisabled?: boolean
  disablePDFExport: boolean

  /**
   * Client status / availability
   */
  isClientDisabled: boolean // Will be true in case client is not available

  /**
   * Order status
   */
  status: string
  statusEnumValue: string
  possibleUpdateOptionsForStatus: Array<IOption>
  isEditStatusDisabled: boolean

  /**
   * SAP fields
   */
  sapStatus: string // Will be either "Sent" / "Received" / "Not received"
  sapStatusColor: string
  isSapStatusBold: boolean
  canSendToSap: boolean // Will be true in case order status is validated
  disableSendToSap: boolean
  sapOrderNumber: string

  /**
   * Operations on order
   */
  isCancelProductStatusDisabled: boolean
  isDeleteStatusDisabled: boolean
  hasInactiveProductOrSizeWithQty: boolean

  isClientActive: boolean
  isDoorActive: boolean
}

export interface IClient {
  id: string
  name: string
  code: string
  countryCode: string
  currency: string
  active: boolean
  zoneCode?: string
}

export interface IClientOrder {
  id: string
  orderName: string
  orderType: string
  orderId: string
  shipTo: string
  orderValue: number
  CreatedBy: ISalesAssistant
  creationDate: number
  globalValue: number
  deliveryDate: number
  doorOrders: Array<IDoorOrder>
  Client: IClient
  Sessions: Array<IActivity>
  internalClient: boolean
  status: string
  sapStatus: string
  sapOrderNumber: string
  LastUpdatedAt: number
  hasInactiveDoor?: boolean
  hasInactiveProductOrSizeWithQty?: boolean
  salesOfficeDescription: string
  salesAssistant: string
  orderQuantity: string
  activityIds: Array<any>
  isClientMaster: boolean
  buyingSession: any
  orderCurrency: string
}

export interface IDoorOrder {
  Id: string
  CreatedBy: ISalesAssistant
  CreationDate: string
  OrderNumber: string
  TotalQty: number
  TotalSizedQty: number
  SizedValue: number
  TotalValue: number
  DeliveryDate: number
  Status: string
  Door: IDoor
  SapStatus: string
  SapOrderId: string
  LastUpdatedAt: number
  HasInactiveProductOrSizeWithQty?: boolean
  id?: string
  hasInactiveProductOrSizeWithQty?: boolean
  doorActive?: boolean
}

export interface ISalesAssistant {
  Name: string
}

export interface IActivity {
  Activity: CentricNode
}

export interface IBuyingSessionGroup {
  Id: string
  BuyingSessionGroupStatus: string
  Gender: CentricNode
  ExcludedClients: Array<Partial<CentricNode>>
  DeliveryWindow?: IBuyingGroupDeliveryWindow
  Sessions?: Array<IStandardBuyingSession>
}

export interface OrderBaseDetails {
  sessions: Array<any>
  clientName: string
  isClientUnavailable: boolean
}

export const getDoorsList = data => {
  const doorsList = []
  forEach(data, ({ Doors }) =>
    forEach(Doors, ({ Id, Name, IsActive }) => {
      if (IsActive) {
        doorsList.push({ Id, Name })
      }
    })
  )
  return doorsList
}

export const getInstructionText = (maxLimit: number, data: Array<any> = []) => {
  return `Status change can be applied only to maximum of ${maxLimit} ${
    maxLimit > 1 ? 'records' : 'record'
  }. Currently ${data.length} ${
    data.length > 1 ? 'records' : 'record'
  } are present in table.`
}

export const getStatusesList = memoize(statuses => {
  let valueColorMap = config?.appConfig?.enumerations?.orderStatus?.VALUE_COLOR_MAP ?? {}
  const options = []
  forEach(statuses, status => {
    const option = {
      ...status,
      color: valueColorMap[status?.value]
    }
    options.push(option)
  })
  return options
}, isEqual)

export const isDisabledBulkStatusIcon = (
  data,
  key,
  status,
  filteredIds,
  possibleUpdateOptionsForStatus,
  deletedOrdersTabView
) => {
  const groupByStatus = keys(groupBy(data, (item: any) => item[key])).length
  const filteredRows = filter(data, data => data[key] === status)
  if (
    deletedOrdersTabView ||
    groupByStatus > 1 ||
    !filteredIds?.length ||
    !filteredRows?.length ||
    !data?.length ||
    !possibleUpdateOptionsForStatus.length ||
    key !== 'status'
  ) {
    return true
  }
}

export const isDisabledBulkSendToSAPIcon = (filteredRows, filteredIds) => {
  let validatedStatusValue =
    config?.appConfig?.enumerations?.orderStatus?.POSSIBLE_VALUES_FOR_SEND_TO_SAP
  const rows = filter(filteredRows, data =>
    includes(validatedStatusValue, data?.statusEnumValue)
  )
  if (!filteredIds?.length || !rows.length) {
    return true
  }
}

export const canUpdateOrderStatus = order => {
  if (order?.orderType === OrderType.Client) {
    return order?.isClientActive && !order?.clientHasInactiveDoor
  }
  if (order?.orderType === OrderType.Door) {
    return order?.isDoorActive && order?.isClientActive
  }
}

export const getFilteredData = (data, selectedRows) => {
  let filteredData = []
  const selectedData = []
  forEach(data, record => {
    filteredData.push(record)
    const index = findIndex(selectedRows, { id: record.id })
    if (index > -1) {
      selectedData.push(record)
    }
  })
  return { filteredData, selectedData }
}

export const getListForModal = (list, titleMessage) => {
  return `<div>
      <Typography>${titleMessage}</Typography>
      <ul>
      ${map(list, item => `<li>${item}</li>`).join('')}
      </ul>
    </div>`
}

export const createValidationErrorMessage = (error, descText): string => {
  return getListForModal(
    Object.keys(error).map(orderId =>
      getListForModal(createOrderErrorMessage(error[orderId]), `Order No: ${orderId}`)
    ),
    descText
  )
}

const createOrderErrorMessage = (errors: any): string[] => {
  return Object.keys(errors).map(errorId =>
    getListForModal(errors[errorId], strings.orderValidationErrorKeys[errorId])
  )
}

export const getStatusData = memoize((rowData, key) => {
  const possibleUpdateOptionsForStatus = rowData?.possibleUpdateOptionsForStatus
  const status = rowData?.[key]
  const statusEnumValue = rowData?.statusEnumValue
  return {
    status,
    possibleUpdateOptionsForStatus,
    statusEnumValue
  }
}, isEqual)

export const getDoorOrdersList = memoize(orderData => {
  return orderData ? [orderData] : []
}, isEqual)

const hasSAPResponseTimeElapsed = (orderUpdatedAt: number) => {
  return (new Date().getTime() - new Date(orderUpdatedAt).getTime()) / 60000 > 90
}

export const getExchangeRateForClientCurrency = memoize(
  (clientCurrency: string, exchangeRateTable) => {
    // If client currency is same as currency to, return 1 as exchange rate
    if (clientCurrency === exchangeRateTable?.To?.[0]?.Name) {
      return 1
    }

    let exchangeRateRecord = find(
      exchangeRateTable?.To?.[0]?.Value?.Rates ?? [],
      exchangeRecord => exchangeRecord?.Name === clientCurrency
    )
    return exchangeRateRecord?.Value ?? null
  }
)

const CanZamUpdateOrderStatus = (orderStatus, isZAMUserLoggedIn) => {
  return (
    orderStatus ===
      config?.appConfig?.enumerations?.orderStatus?.ORDER_STATUS_VALUES.Validated &&
    isZAMUserLoggedIn
  )
}

const getClientOrderDetails = ({
  order,
  statusEnumValues,
  possibleUpdateValuesForSendToSAP,
  possibleUpdateValuesForStatus,
  sentToSAPEnumValue,
  isClientExcluded,
  isRTLBSGPaused,
  isBSGClosed
}: {
  order: IClientOrder
  statusEnumValues: Array<any>
  possibleUpdateValuesForStatus: any
  possibleUpdateValuesForSendToSAP: Array<string>
  sentToSAPEnumValue: string
  isClientExcluded: boolean
  isRTLBSGPaused: boolean
  isBSGClosed: boolean
}): Partial<IOrderManagementTableData> => {
  const statusEnumOptionMap = getEnumOptionMap(statusEnumValues)
  const isZAMUserLoggedIn = isZAM()
  const isWholesaleCMAdminOrZAM = !!isWholesaleCMOrAdmin() || !!isZAMUserLoggedIn
  const isRetailCMorAdmin = !!isRetailCMOrAdmin()
  const canSendToSap =
    includes(possibleUpdateValuesForSendToSAP, order?.status) &&
    !order?.sapStatus &&
    !isZAMUserLoggedIn
  const sapStatus = order?.sapStatus
    ? order?.sapStatus === strings.sapSuccess
      ? SAPStatusDescription.RECEIVED
      : SAPStatusDescription.FAILED
    : canSendToSap
    ? isWholesaleCMOrAdmin()
      ? SAPStatusDescription.SEND
      : ''
    : order?.status === sentToSAPEnumValue
    ? hasSAPResponseTimeElapsed(order.LastUpdatedAt)
      ? SAPStatusDescription.NOT_RECEIVED
      : SAPStatusDescription.SENT
    : ''
  const orderStatusValues: any =
    config?.appConfig?.enumerations?.orderStatus?.ORDER_STATUS_VALUES ?? {}

  return {
    id: order?.id,
    buyingSessionId: order?.buyingSession?.id ?? '',
    possibleUpdateOptionsForStatus: getPossibleUpdateOptionsForStatus(
      statusEnumOptionMap,
      order?.status,
      orderStatusValues,
      possibleUpdateValuesForStatus,
      sapStatus
    ),
    /**
     * Disable edit status field
     */
    isEditStatusDisabled:
      CanZamUpdateOrderStatus(order?.status, isZAMUserLoggedIn) ||
      isClientExcluded ||
      isRTLBSGPaused ||
      isBSGClosed ||
      (order.internalClient && !isRetailCMorAdmin) ||
      (!order.internalClient && !isWholesaleCMAdminOrZAM),
    /**
     * SAP fields
     */
    canSendToSap,
    disableSendToSap: !isWholesaleCMOrAdmin() || isBSGClosed,
    sapStatus,
    sapStatusColor: SAPStatusDescriptionColorMapping[sapStatus],
    isSapStatusBold: sapStatus === SAPStatusDescription.FAILED,
    hasInactiveProductOrSizeWithQty: order?.hasInactiveProductOrSizeWithQty || false
  }
}

const getPossibleUpdateOptionsForStatus = (
  statusEnumOptionMap,
  orderStatus,
  orderStatusValues,
  possibleUpdateValuesForStatus,
  sapStatus
): Array<IOption> => {
  if (
    sapStatus !== SAPStatusDescription.FAILED &&
    orderStatus === orderStatusValues?.['Sent to SAP']
  ) {
    return []
  } else if (!isEmpty(statusEnumOptionMap)) {
    return compact(
      (possibleUpdateValuesForStatus[orderStatus] || []).map(
        enumValue => statusEnumOptionMap[enumValue]
      )
    )
  } else {
    return []
  }
}

const getDoorOrderDetails = (
  order: IClientOrder,
  statusEnumValues: Array<any>,
  possibleUpdateValuesForStatus: any,
  possibleUpdateValuesForSendToSAP: Array<string>,
  sentToSAPEnumValue: string,
  isBSGClosed: boolean
): Partial<IOrderManagementTableData> => {
  const isZAMUserLoggedIn = isZAM()
  const statusEnumOptionMap = getEnumOptionMap(statusEnumValues)
  const canSendToSap =
    includes(possibleUpdateValuesForSendToSAP, order?.status) && !order?.sapStatus

  const sapStatus = order?.sapStatus
    ? order?.sapStatus === strings.sapSuccess
      ? SAPStatusDescription.RECEIVED
      : SAPStatusDescription.FAILED
    : canSendToSap
    ? isZAMUserLoggedIn
      ? ''
      : SAPStatusDescription.SEND
    : order?.status === sentToSAPEnumValue
    ? hasSAPResponseTimeElapsed(order.LastUpdatedAt)
      ? SAPStatusDescription.NOT_RECEIVED
      : SAPStatusDescription.SENT
    : ''

  const orderStatusValues: any =
    config?.appConfig?.enumerations?.orderStatus?.ORDER_STATUS_VALUES ?? {}

  return {
    id: order?.doorOrders?.[0].id,
    clientOrderId: order?.id,
    possibleUpdateOptionsForStatus: getPossibleUpdateOptionsForStatus(
      statusEnumOptionMap,
      order?.status,
      orderStatusValues,
      possibleUpdateValuesForStatus,
      sapStatus
    ),
    /**
     * SAP fields
     */
    canSendToSap,
    disableSendToSap: !isWholesaleCMOrAdmin() || isBSGClosed,
    sapStatus,
    sapStatusColor: SAPStatusDescriptionColorMapping[sapStatus],
    isSapStatusBold: sapStatus === SAPStatusDescription.FAILED,
    isDoorActive: order?.doorOrders?.[0].doorActive ?? false,
    hasInactiveProductOrSizeWithQty:
      order?.doorOrders?.[0].hasInactiveProductOrSizeWithQty || false
  }
}

const getActivitiesAndGender = (buyingSessionGroup, orderActivities) => {
  let activityGender = []
  if (isEmpty(buyingSessionGroup)) return []
  if (!orderActivities?.length) return []
  const gender = buyingSessionGroup?.gender ?? ''
  let activities = buyingSessionGroup?.activities
  activities = activities.filter(activity => {
    return orderActivities.some(id => activity.id === id)
  })
  for (let activity of activities) {
    const label = `${gender} ${activity.name}`
    activityGender.push({
      label,
      value: label
    })
  }
  return activityGender
}

const getDoorData = doorOrders => {
  let doorOrderName = []
  for (let door of doorOrders) {
    doorOrderName.push({
      label: door?.doorName,
      value: door?.doorId
    })
  }
  return doorOrderName
}

const disablePDFExport = orderStatusEnum => {
  const orderConfirmedStatus =
    config?.appConfig?.enumerations?.orderStatus?.ALLOWED_PDF_EXPORT_FOR_VALUES
  return !includes(orderConfirmedStatus, orderStatusEnum)
}

class OrderManagementService {
  processOrderData = memoize(
    (
      {
        orders,
        statusEnumValues,
        buyingSessionGroup
      }: {
        orders: Array<IClientOrder>
        statusEnumValues: Array<any>
        buyingSessionGroup: any
      },
      t: any,
      isRTLOrderView: boolean
    ): Array<IOrderManagementTableData> => {
      if (isEmpty(orders)) return []
      const processedOrders = []
      let orderObj = {}
      const restrictDeleteForOrderStatuses =
        config?.appConfig?.enumerations?.orderStatus?.RESTRICTED_DELETE_FOR_VALUES ?? []
      const possibleUpdateValuesForRtl =
        transformConfig(
          config?.appConfig?.enumerations?.orderStatus.POSSIBLE_UPDATE_OPTIONS_MAP_FOR_RTL
        ) ?? {}
      const possibleUpdateValuesForWhl =
        transformConfig(
          config?.appConfig?.enumerations?.orderStatus.POSSIBLE_UPDATE_OPTIONS_MAP_FOR_WHL
        ) ?? {}
      const possibleUpdateValuesForRtlClosedBS =
        config?.appConfig?.enumerations?.orderStatus
          ?.POSSIBLE_UPDATE_OPTIONS_MAP_WITH_NEXT_STATUS_FOR_RTL ?? {}
      const possibleUpdateValuesForWhlClosedBS =
        config?.appConfig?.enumerations?.orderStatus
          ?.POSSIBLE_UPDATE_OPTIONS_MAP_WITH_NEXT_STATUS_FOR_WHL ?? {}

      const possibleUpdateValuesForSendToSAP =
        config?.appConfig?.enumerations?.orderStatus?.POSSIBLE_VALUES_FOR_SEND_TO_SAP ??
        []
      const sentToSAPEnumValue =
        config?.appConfig?.enumerations?.orderStatus?.ORDER_STATUS_VALUES?.[
          strings.sentToSAP
        ] ?? []

      const buyingSessionStatusValueMap =
        config?.appConfig?.enumerations?.buyingSessionStatus
          ?.BUYING_SESSION_STATUS_VALUES || {}
      const retailBuyingSessionStatusValueMap =
        config?.appConfig?.enumerations?.retailBuyingSessionStatus
          .BUYING_SESSION_STATUS_VALUES || {}

      let hasDeliveryWindowPassed = false
      if (buyingSessionGroup?.defaultDeliveryWindow?.End) {
        const deliveryWindowCancelDate = new Date(
          buyingSessionGroup?.defaultDeliveryWindow?.End
        ).setHours(0, 0, 0, 0)
        const currentDate = new Date().setHours(0, 0, 0, 0)
        hasDeliveryWindowPassed =
          new Date(currentDate).getTime() > new Date(deliveryWindowCancelDate).getTime()
      }
      const globalCurrency = config.globalCurrency || DefaultConfig.GLOBAL_CURRENCY
      const isZAMUserLoggedIn = isZAM()
      const isWholesaleCMAdminOrZAM = !!isWholesaleCMOrAdmin() || !!isZAMUserLoggedIn
      const isRetailCMorAdmin = !!isRetailCMOrAdmin()
      const isAdminUser = !!isAdmin()
      const orderStatusValues: any =
        config?.appConfig?.enumerations?.orderStatus?.ORDER_STATUS_VALUES ?? {}
      for (let order of orders) {
        const isClientExcluded =
          order?.Client?.id &&
          includes(
            map(
              buyingSessionGroup?.excludedClients || [],
              clientObject => clientObject.Id
            ),
            order?.Client?.id
          )
        const isWHLBSGClosed =
          buyingSessionGroup?.buyingSessionGroupStatus ===
          buyingSessionStatusValueMap[strings.closed.toUpperCase()]
        const isRTLBSGClosed =
          buyingSessionGroup?.retailBuyingSessionGroupStatus ===
          retailBuyingSessionStatusValueMap[strings.closed.toUpperCase()]

        const orderCurrency = order?.orderCurrency
        const isClientMaster = order?.isClientMaster
        const orderType = order?.orderType?.toLowerCase()
        const isRTLBSGPaused = isRTLOrderView
          ? buyingSessionGroup?.retailBuyingSessionGroupStatus ===
            retailBuyingSessionStatusValueMap[strings.pause.toUpperCase()]
          : false

        const hasDeliveryWindowZones = includes(
          config?.deliveryWindowExcludedZones?.split(',') || [],
          order?.Client?.zoneCode
        )

        orderObj = {
          name: order?.Client?.name || 'NA',
          clientId: order?.Client?.id,
          code: order?.Client?.code || 'NA',
          isClientDisabled: isClientExcluded,
          isClientActive: order?.Client?.active ?? false,
          clientHasInactiveDoor: order?.hasInactiveDoor ?? false,
          zam: order?.salesOfficeDescription || 'NA',
          country: order?.Client?.countryCode || 'NA',
          deliveryWindow: {
            ...buyingSessionGroup?.defaultDeliveryWindow,
            End: hasDeliveryWindowZones
              ? undefined
              : buyingSessionGroup?.defaultDeliveryWindow?.End
          },
          activityGender: getActivitiesAndGender(buyingSessionGroup, order?.activityIds),
          orderType: orderType,
          internalClient: order?.internalClient || false,
          isClientMaster,
          doors: getDoorData(order?.doorOrders),
          salesAssistant: order?.salesAssistant,
          shipTo: order?.shipTo || '',
          disablePDFExport: disablePDFExport(order?.status ?? ''),
          date: order?.creationDate ? formatDateByLocale(order?.creationDate) : 'NA',
          orderNumber: order?.orderId,
          qty: formatNumberByLocale(order?.orderQuantity),
          value: formatCurrencyValueByLocale(orderCurrency, order?.orderValue, 2),
          globalValue: globalCurrency
            ? formatCurrencyValueByLocale(globalCurrency, order?.globalValue, 2)
            : 'NA',
          deliveryDate: order.deliveryDate
            ? (getUTCFromTimeStamp(order.deliveryDate, 'DD/MM/YYYY') as string)
            : null,
          isDeliveryDateDisabled: hasDeliveryWindowZones
            ? !hasDeliveryWindowZones ||
              (order?.status !== orderStatusValues.Booking &&
                order?.status !== orderStatusValues['In Process'])
            : (!order.internalClient && !isWholesaleCMAdminOrZAM) ||
              (order?.status !== orderStatusValues.Booking &&
                order?.status !== orderStatusValues['In Process']) ||
              isClientExcluded ||
              isWHLBSGClosed ||
              hasDeliveryWindowPassed,
          status: getEnumDescriptionFromValue(order?.status ?? '', statusEnumValues),
          statusEnumValue: order?.status ?? '',
          isCancelProductStatusDisabled:
            includes(restrictDeleteForOrderStatuses, order?.status) ||
            isRTLBSGPaused ||
            (isRTLOrderView ? isRTLBSGClosed : isWHLBSGClosed) ||
            (order.internalClient && !isRetailCMorAdmin) ||
            (!order.internalClient && !isWholesaleCMAdminOrZAM),
          isDeleteStatusDisabled:
            includes(restrictDeleteForOrderStatuses, order?.status) ||
            isRTLBSGPaused ||
            (isRTLOrderView ? isRTLBSGClosed : isWHLBSGClosed) ||
            (order.internalClient && !isRetailCMorAdmin) ||
            (!order.internalClient && !isWholesaleCMAdminOrZAM) ||
            (order.internalClient && !isAdminUser),
          isEditStatusDisabled:
            CanZamUpdateOrderStatus(order?.status, isZAMUserLoggedIn) ||
            isClientExcluded ||
            isRTLBSGPaused ||
            (isRTLOrderView ? isRTLBSGClosed : isWHLBSGClosed) ||
            (order.internalClient && !isRetailCMorAdmin) ||
            (!order.internalClient && !isWholesaleCMAdminOrZAM),
          sapOrderNumber: order?.sapOrderNumber
        }

        if (isClientMaster) {
          orderObj = {
            ...orderObj,
            ...getClientOrderDetails({
              order,
              statusEnumValues,
              possibleUpdateValuesForStatus: isRTLOrderView
                ? isRTLBSGClosed
                  ? possibleUpdateValuesForRtlClosedBS
                  : possibleUpdateValuesForRtl
                : isWHLBSGClosed
                ? possibleUpdateValuesForWhlClosedBS
                : possibleUpdateValuesForWhl,
              possibleUpdateValuesForSendToSAP,
              sentToSAPEnumValue,
              isClientExcluded,
              isRTLBSGPaused,
              isBSGClosed: isRTLOrderView ? isRTLBSGClosed : isWHLBSGClosed
            })
          }
          processedOrders.push(orderObj)
        } else {
          /**
           * Since following flag will be same for all door orders, evaluate it once
           */
          orderObj = {
            ...orderObj,
            ...getDoorOrderDetails(
              order,
              statusEnumValues,
              isWHLBSGClosed
                ? possibleUpdateValuesForWhlClosedBS
                : possibleUpdateValuesForWhl,
              possibleUpdateValuesForSendToSAP,
              sentToSAPEnumValue,
              isWHLBSGClosed
            )
          }
          processedOrders.push(orderObj)
        }
      }
      return processedOrders
    },
    isEqual
  )

  processAndSearchOrderData = memoize(
    (
      orderDataWithStatusEnums: OrderDataWithStatusEnums,
      searchKey: string = '',
      columnDefList: Array<TableViewColumnDefinition>,
      t: any,
      isRTLOrderView?: boolean
    ) => {
      let processedOrderData = this.processOrderData(
        orderDataWithStatusEnums as any,
        t,
        isRTLOrderView
      )

      /**
       * If search key is present filter clients
       */
      if (searchKey) {
        return searchData(
          processedOrderData,
          searchKey,
          columnDefList.map(col => col.node.key)
        )
      }

      return processedOrderData
    }
  )
}

export const orderManagementService = new OrderManagementService()
